import { z } from 'zod';

// Pinned Graph API version for every Marketing API call. Meta versions are supported for ~2 years
// from release; bump deliberately (and re-run the integration tests) rather than floating on
// "latest". Verified against developers.facebook.com/docs/marketing-api on 2026-09-15.
export const META_MARKETING_API_VERSION = 'v25.0';
export const META_GRAPH_API_BASE_URL = `https://graph.facebook.com/${META_MARKETING_API_VERSION}`;

// Brkthru's Meta Campaign Types (CONTEXT.md "Objective"): the two objectives a Meta buy is built
// from. These are the card-facing values; `metaCampaignObjective` maps them to the API enum.
export const MetaObjective = z.union([z.literal('awareness'), z.literal('traffic')]);
export type MetaObjective = z.infer<typeof MetaObjective>;

export const MetaCampaignObjective = z.union([
  z.literal('OUTCOME_AWARENESS'),
  z.literal('OUTCOME_TRAFFIC')
]);
export type MetaCampaignObjective = z.infer<typeof MetaCampaignObjective>;

// Traffic campaigns choose between landing-page views and link clicks (client/trader preference);
// Awareness is always IMPRESSIONS. Meta cannot change an ad set's goal after save.
export const MetaTrafficPerformanceGoal = z.union([
  z.literal('LANDING_PAGE_VIEWS'),
  z.literal('LINK_CLICKS')
]);
export type MetaTrafficPerformanceGoal = z.infer<typeof MetaTrafficPerformanceGoal>;

export const MetaOptimizationGoal = z.union([z.literal('IMPRESSIONS'), MetaTrafficPerformanceGoal]);
export type MetaOptimizationGoal = z.infer<typeof MetaOptimizationGoal>;

// CREDIT is deprecated in favour of FINANCIAL_PRODUCTS_SERVICES; Meta rejects it on create.
export const MetaSpecialAdCategory = z.union([
  z.literal('HOUSING'),
  z.literal('EMPLOYMENT'),
  z.literal('FINANCIAL_PRODUCTS_SERVICES'),
  z.literal('ISSUES_ELECTIONS_POLITICS')
]);
export type MetaSpecialAdCategory = z.infer<typeof MetaSpecialAdCategory>;

export const MetaBidStrategy = z.literal('LOWEST_COST_WITHOUT_CAP');
export type MetaBidStrategy = z.infer<typeof MetaBidStrategy>;

export const MetaEntityStatus = z.union([
  z.literal('ACTIVE'),
  z.literal('PAUSED'),
  z.literal('DELETED'),
  z.literal('ARCHIVED')
]);
export type MetaEntityStatus = z.infer<typeof MetaEntityStatus>;

export const MetaPublisherPlatform = z.union([z.literal('facebook'), z.literal('instagram')]);
export type MetaPublisherPlatform = z.infer<typeof MetaPublisherPlatform>;

// The only placements a Brkthru ad set runs on (trader spec): the four feeds. Anything beyond
// these raises CPM and is surfaced as a post-create warning.
export const META_FEED_PLACEMENTS = {
  publisher_platforms: ['facebook', 'instagram'],
  facebook_positions: ['feed', 'profile_feed'],
  instagram_positions: ['stream', 'profile_feed']
} as const;

// Meta's minimum ad audience age for most regions; the card default.
export const META_DEFAULT_AGE_MIN = 18;
// Meta's ceiling on age_min (65+ is the top bracket); the card gates on the same bound.
export const META_MAX_AGE_MIN = 65;

export const CreateMetaCampaignPayload = z.object({
  name: z.string().describe('Campaign name'),
  objective: MetaCampaignObjective.describe(
    'OUTCOME_AWARENESS or OUTCOME_TRAFFIC — set from the campaign group objective.'
  ),
  lifetime_budget_dollars: z
    .number()
    .positive()
    .describe(
      'Campaign-level lifetime budget in dollars (Advantage campaign budget). Converted to the ' +
        'ad account currency minor unit at API call time. Never a daily budget.'
    ),
  special_ad_categories: z
    .array(MetaSpecialAdCategory)
    .default([])
    .describe(
      'Declared special ad categories; empty unless the client is in a regulated vertical.'
    ),
  buying_type: z.literal('AUCTION').default('AUCTION'),
  status: z.literal('PAUSED').default('PAUSED').describe('Always created PAUSED for QA.'),
  bid_strategy: MetaBidStrategy.optional().describe(
    'Always LOWEST_COST_WITHOUT_CAP (highest volume) for both objectives: omitting it makes Meta ' +
      'require a bid_amount on the ad set under a campaign budget.'
  )
});
export type CreateMetaCampaignPayload = z.infer<typeof CreateMetaCampaignPayload>;

export const MetaGeoKey = z.object({
  key: z.string().min(1).describe('Meta geo location key from the adgeolocation search.'),
  name: z.string().optional().describe('Display name for the preview card; not sent to Meta.')
});
export type MetaGeoKey = z.infer<typeof MetaGeoKey>;

export const MetaGeoCity = MetaGeoKey.extend({
  radius: z.number().positive().optional(),
  distance_unit: z.union([z.literal('mile'), z.literal('kilometer')]).optional()
});
export type MetaGeoCity = z.infer<typeof MetaGeoCity>;

export const MetaGeoLocations = z.object({
  countries: z.array(z.string().length(2)).optional().describe('ISO 3166-1 alpha-2 codes.'),
  regions: z.array(MetaGeoKey).optional(),
  cities: z.array(MetaGeoCity).optional(),
  zips: z.array(MetaGeoKey).optional().describe('Keys are "<country>:<zip>", e.g. "US:62704".')
});
export type MetaGeoLocations = z.infer<typeof MetaGeoLocations>;

export const MetaAdSetTargeting = z.object({
  geo_locations: MetaGeoLocations,
  age_min: z
    .number()
    .int()
    .min(META_DEFAULT_AGE_MIN)
    .max(META_MAX_AGE_MIN)
    .default(META_DEFAULT_AGE_MIN),
  // No `genders` / `locales`: omitting them targets all genders and all languages.
  publisher_platforms: z.array(MetaPublisherPlatform),
  facebook_positions: z.array(z.string()),
  instagram_positions: z.array(z.string()),
  targeting_automation: z
    .object({ advantage_audience: z.literal(0) })
    .describe('Advantage+ audience off; Meta silently re-enables it, so create-time is explicit.')
});
export type MetaAdSetTargeting = z.infer<typeof MetaAdSetTargeting>;

export const CreateMetaAdSetPayload = z.object({
  name: z.string().describe('Ad set name'),
  optimization_goal: MetaOptimizationGoal.describe(
    'IMPRESSIONS for Awareness; LANDING_PAGE_VIEWS or LINK_CLICKS for Traffic.'
  ),
  billing_event: z.literal('IMPRESSIONS').default('IMPRESSIONS'),
  destination_type: z
    .literal('WEBSITE')
    .optional()
    .describe('Traffic only: the conversion location. Omitted for Awareness.'),
  start_time: z
    .string()
    .describe('ISO 8601 datetime with offset, e.g. 2026-10-01T00:00:00-0500 (ad account tz).'),
  end_time: z
    .string()
    .describe(
      'ISO 8601 datetime with offset; the last minute of the flight end day in account tz.'
    ),
  status: z.literal('PAUSED').default('PAUSED').describe('Always created PAUSED for QA.'),
  promoted_object: z.object({
    page_id: z.string().min(1).describe('The Page the ads run as (identity), from the card.')
  }),
  targeting: MetaAdSetTargeting
});
export type CreateMetaAdSetPayload = z.infer<typeof CreateMetaAdSetPayload>;

// ---------------------------------------------------------------------------
// Read envelopes. Loose on purpose: Graph API responses carry only the fields requested via
// `fields=`, and the verification step reads a handful of them. Money fields come back as minor-
// unit strings.
// ---------------------------------------------------------------------------
export const MetaCreateResponse = z.looseObject({ id: z.string() });
export type MetaCreateResponse = z.infer<typeof MetaCreateResponse>;

export const MetaAdAccount = z.looseObject({
  id: z.string().describe('act_-prefixed id'),
  account_id: z.string().optional().describe('Bare numeric id'),
  name: z.string().optional(),
  currency: z.string().optional(),
  timezone_name: z.string().optional(),
  account_status: z.number().optional()
});
export type MetaAdAccount = z.infer<typeof MetaAdAccount>;

export const MetaPage = z.looseObject({
  id: z.string(),
  name: z.string().optional()
});
export type MetaPage = z.infer<typeof MetaPage>;

export const MetaCampaign = z.looseObject({
  id: z.string(),
  name: z.string().optional(),
  objective: z.string().optional(),
  status: z.string().optional(),
  effective_status: z.string().optional(),
  buying_type: z.string().optional(),
  bid_strategy: z.string().optional(),
  lifetime_budget: z.string().optional(),
  daily_budget: z.string().optional(),
  special_ad_categories: z.array(z.string()).optional(),
  start_time: z.string().optional(),
  stop_time: z.string().optional()
});
export type MetaCampaign = z.infer<typeof MetaCampaign>;

export const MetaAdSet = z.looseObject({
  id: z.string(),
  name: z.string().optional(),
  campaign_id: z.string().optional(),
  status: z.string().optional(),
  effective_status: z.string().optional(),
  optimization_goal: z.string().optional(),
  billing_event: z.string().optional(),
  destination_type: z.string().optional(),
  lifetime_budget: z.string().optional(),
  daily_budget: z.string().optional(),
  start_time: z.string().optional(),
  end_time: z.string().optional(),
  promoted_object: z.looseObject({ page_id: z.string().optional() }).optional(),
  // The fields the post-create verification compares against the sent constants.
  targeting: z
    .looseObject({
      geo_locations: z.looseObject({}).optional(),
      age_min: z.number().optional(),
      age_max: z.number().optional(),
      publisher_platforms: z.array(z.string()).optional(),
      facebook_positions: z.array(z.string()).optional(),
      instagram_positions: z.array(z.string()).optional(),
      targeting_automation: z.looseObject({ advantage_audience: z.number().optional() }).optional()
    })
    .optional()
});
export type MetaAdSet = z.infer<typeof MetaAdSet>;

export const MetaGeoLocationSearchResult = z.looseObject({
  key: z.string(),
  name: z.string(),
  type: z.string().describe('country | region | city | zip | ...'),
  country_code: z.string().optional(),
  country_name: z.string().optional(),
  region: z.string().optional(),
  region_id: z.number().optional(),
  supports_region: z.boolean().optional(),
  supports_city: z.boolean().optional()
});
export type MetaGeoLocationSearchResult = z.infer<typeof MetaGeoLocationSearchResult>;

export const MetaPagedResponse = <T extends z.ZodTypeAny>(item: T) =>
  z.looseObject({
    data: z.array(item),
    paging: z
      .looseObject({
        cursors: z
          .looseObject({ before: z.string().optional(), after: z.string().optional() })
          .optional(),
        next: z.string().optional()
      })
      .optional()
  });

const AD_ACCOUNT_PREFIX = 'act_';

// Ads Manager shows ad account ids bare ("1234567890"); the API addresses them as "act_1234567890".
// Accept either (with stray whitespace) and always return the prefixed form.
export function normalizeMetaAdAccountId(raw: string): string {
  const trimmed = raw.trim();
  return trimmed.startsWith(AD_ACCOUNT_PREFIX) ? trimmed : `${AD_ACCOUNT_PREFIX}${trimmed}`;
}

// Ads Manager URLs (`act=`) and the ad account `account_id` field use the bare numeric id.
export function stripMetaAdAccountPrefix(raw: string): string {
  const trimmed = raw.trim();
  return trimmed.startsWith(AD_ACCOUNT_PREFIX) ? trimmed.slice(AD_ACCOUNT_PREFIX.length) : trimmed;
}

// Run on either form: the digits after an optional act_ prefix must be non-empty.
export function isValidMetaAdAccountId(raw: string): boolean {
  return /^\d+$/.test(stripMetaAdAccountPrefix(raw));
}

export function metaCampaignObjective(objective: MetaObjective): MetaCampaignObjective {
  return objective === 'awareness' ? 'OUTCOME_AWARENESS' : 'OUTCOME_TRAFFIC';
}

export function metaOptimizationGoal(
  objective: MetaObjective,
  performanceGoal?: MetaTrafficPerformanceGoal
): MetaOptimizationGoal {
  if (objective === 'awareness') return 'IMPRESSIONS';
  return performanceGoal ?? 'LANDING_PAGE_VIEWS';
}

// Meta budgets are integers in the ad account currency's minor unit (cents for USD). Assumes a
// two-decimal currency; the ad account's `currency` is fetched at propose time so a zero-decimal
// currency can be rejected there.
export function dollarsToMetaMinorUnits(dollars: number): string {
  return String(Math.round(dollars * 100));
}

export function metaMinorUnitsToDollars(minorUnits: string | number): number {
  const n = typeof minorUnits === 'string' ? Number(minorUnits) : minorUnits;
  if (!Number.isFinite(n)) {
    throw new Error(`Invalid Meta minor-unit amount '${minorUnits}'`);
  }
  return n / 100;
}
